import i18nFactory from '@yandex-int/i18n';
import { compact, flatten, intersection, pull, range, uniq, uniqBy } from 'lodash';
import { createSelector } from 'reselect';

import { BulkReplacementRadio } from '../../../components/BulkReplacementModeChooser/BulkReplacementModeChooser';
import { ErrorItem } from '../../../components/ErrorsPresenter/ErrorsPresenter';
import { TrackingParametersReplacementSelect } from '../../../components/TrackingParametersBulkReplacement/TrackingParametersBulkReplacement';
import { EditCPCVideoAdStateProps } from '../../../containers/EditCPCVideoAdContainer/EditCPCVideoAdContainer';
import { EditVideoStateProps } from '../../../containers/EditVideoContainer/EditVideoContainer';
import { mapMassActionDataToPcodeRenderData } from '../../../mappers/banner/banner';
import { RoutesPath } from '../../../routes/index';
import { getCollapsedSiteLinks, getDefaultSiteLinkData } from '../../../service/BannerHelpers/BannerHelpers';
import {
    BannersEditCreativeImageSetKeyType,
    checkCreative,
    convertImageCreativeToSet,
    getAllVisibleBannerSiteLinksDescr,
    getAllVisibleBannerSiteLinksText,
    getAllVisibleBannerSiteLinksUrls,
    getItemFromImageSet,
    getNewBannersSiteLinks,
    getNewBannersSiteLinksForPartLinkReplace,
    getNewBannersSiteLinksForTrackingParametersReplace,
    getSitelinksFullHref,
    hrefIncludesForTrackingParameterReplacement,
    hrefTurbolandingIncludesForTrackingParameterReplacement,
    isBannerEditMassActionsAvailable,
    isBannerEditMassActionsCalloutsAvailable,
    isBannerPriceMassActionsAvailable,
    isBannerTextMassActionsAvailable,
    isIdForMassAction,
    isTextBannerEditMassActionsAvailable,
    isTextReplaceMassActionsAvailable,
    isVideoBannerEditMassActionsAvailable,
    replaceTrackingParameters,
    replaceTurboTrackingParameters,
    replaceUrlPart,
    siteLinksIncludes,
    siteLinksIncludesForTrackingParameterReplacement,
    UrlPart
} from '../../../service/BannersEditMassActionsHelpers/BannersEditMassActionsHelpers';
import { escapeRegexpString } from '../../../service/Escape/Escape';
import { UrlHelpers } from '../../../service/UrlHelpers';
import {
    BannerId,
    BannerSiteLinksId,
    CalloutsEditorId,
    ImageEditorId,
    ImageOrCreativeUploadingMode,
    TurbolandingPage
} from '../../../typings/banners';
import {
    bannerHasSitelinks,
    BannerSiteLinksEditorData,
    BannerVideoData,
    CalloutsEditorData,
    DraftBanner,
    EditedBanner,
    EditedBannerCreative,
    EditedBannerTurbolanding,
    EditedBannerTypeText,
    EditedBannerVideo,
    EditorBannerPriceData,
    MassActionId,
    ProtocolValue
} from '../../../typings/bannersEditState';
import { DropdownChooserItem, EditorPlaceId } from '../../../typings/common';
import { CreativeState } from '../../../typings/creative';
import { AdvType, AdvTypeClient, BannerImageForEdit } from '../../../typings/domain';
import { CurrencyCode } from '../../../typings/graphql-grid';
import { BannerViewType } from '../../../typings/pcode';
import { ClientFeatures, ImageEditorState, State } from '../../../typings/state';
import {
    selectEditableCPCVideoAdsCount,
    selectEditableCPMAudioCount,
    selectEditableCPMBannerCount,
    selectEditableCPMVideoCount,
    selectEditableImageAdsCount,
    selectEditableMcBannerCount,
    selectEditableTextAdsCount
} from '../banner';

import * as keyset from './massActions.i18n';

const i18n = i18nFactory(keyset);

export const selectImageCreativeReplacement = createSelector(
    (state: State) => state.bannersEditMassActions[MassActionId.bannersEdit].data.imageCreative,
    (state: State) => state.imageEditor,
    (imageCreative: BannerImageForEdit[], imageEditor: Record<ImageEditorId, ImageEditorState>) => {
        const imageEditorSet = convertImageCreativeToSet(imageCreative);

        return Object.keys(imageEditor).reduce((acc: ImageEditorState, next: ImageEditorId) => {
            const currentImageEditorData = imageEditor[next].image.data;

            acc[next] = imageEditor[next];

            if (currentImageEditorData) {
                const { height, width, isAdaptive, type: creativeType } = currentImageEditorData;

                const adaptiveType = isAdaptive
                    ? BannersEditCreativeImageSetKeyType.ADAPTIVE
                    : BannersEditCreativeImageSetKeyType.NORMAL;
                const newData = getItemFromImageSet(width, height, adaptiveType, imageEditorSet, creativeType);

                if (newData) {
                    acc[next] = {
                        ...acc[next],
                        image: {
                            ...acc[next].image,
                            displayName: newData.name,
                            imageUrl: newData.url,
                            data: newData,
                            mode: ImageOrCreativeUploadingMode.UPLOADED
                        }
                    };
                }
            }

            return acc;
        }, {});
    }
);

export const selectTypeCreativeMassAction = (state: State): AdvType | undefined =>
    state.bannersEditMassActions.bannersEdit.typeCreative;

export const selectAvailableToChangeVideoCreativeBanners = createSelector(
    (state: State) => state.editBanners.banners,
    (state: State) => state.bannersEditMassActions.bannersEdit.typeCreative,
    (banners: Record<BannerId, EditedBanner>, bannerType: AdvType) => {
        return Object.keys(banners).filter((key: BannerId) =>
            isVideoBannerEditMassActionsAvailable(banners[key], bannerType)
        ).length;
    }
);

export const selectAvailableToChangeCpmCreativeBanners = createSelector(
    (state: State) => state.creative,
    (state: State) => state.editBanners.banners,
    (creative: CreativeState, banners: Record<BannerId, EditedBanner>) => {
        const newCreative = creative[MassActionId.bannersEdit];

        if (newCreative.additionalData) {
            const additionalData = newCreative.additionalData;

            // Считаем подходящие баннеры
            return Object.keys(banners).filter((key: BannerId) =>
                // Проверка - есть ли хотя бы один баннер, который удовлетворял бы требованиям
                additionalData.some(curCreative =>
                    checkCreative(banners[key], { ...newCreative, data: curCreative }, creative[key])
                )
            ).length;
        }

        return Object.keys(banners).filter((key: BannerId) => checkCreative(banners[key], newCreative, creative[key]))
            .length;
    }
);

export const selectAvailableToChangeCreativeBanners = createSelector(
    (state: State) => state.bannersEditMassActions[MassActionId.bannersEdit].data.imageCreative,
    (state: State) => state.imageEditor,
    (imageCreative: BannerImageForEdit[], imageEditor: Record<ImageEditorId, ImageEditorState>) => {
        return Object.keys(imageEditor).reduce((acc: number, key: ImageEditorId) => {
            const currentImageEditorData = imageEditor[key].image ? imageEditor[key].image.data : undefined;

            if (currentImageEditorData && key !== MassActionId.bannersEdit) {
                const {
                    height: bannerCreativeHeight,
                    width: bannerCreativeWidth,
                    isAdaptive: bannerCreativeIsAdaptive,
                    type: bannerCreativeType
                } = currentImageEditorData;
                const canChangeCreative = imageCreative.some(
                    item =>
                        item.height === bannerCreativeHeight &&
                        item.width === bannerCreativeWidth &&
                        item.isAdaptive === bannerCreativeIsAdaptive &&
                        item.type === bannerCreativeType
                );

                if (canChangeCreative) {
                    acc++;
                }
            }

            return acc;
        }, 0);
    }
);

export const selectImageAdsCount = createSelector(
    (state: State) => state.editBanners.banners,
    (banners: Record<BannerId, EditedBanner>) => {
        const keys = Object.keys(banners);

        return keys.filter(key => {
            const banner: EditedBanner = banners[key];

            return (
                banner.isEditAllow && (banner.bannerType === AdvType.IMAGE_AD || banner.bannerType === AdvType.MCBANNER)
            );
        }).length;
    }
);

export const selectMassActionsTurbolandingEditorButtonAvailable = createSelector(
    (state: State) => state.bannersEditMassActions[MassActionId.bannersEdit].data.currentActiveRadioButtonTubolandings,
    (state: State) => (state.turbolandings[MassActionId.bannersEdit] || {}).page,
    (currentActiveRadioButtonTubolandings: BulkReplacementRadio, page?: TurbolandingPage) => {
        if (page && currentActiveRadioButtonTubolandings === BulkReplacementRadio.REPLACE) {
            return true;
        }

        if (currentActiveRadioButtonTubolandings === BulkReplacementRadio.DELETE) {
            return true;
        }

        return false;
    }
);

export const selectEditableTextAdsIds = createSelector(
    (state: State) => state.editBanners.banners,
    (state: State) => state.grid.selectionInfo.banners,
    (state: State) => state.router.route,
    (banners: Record<BannerId, EditedBanner>, gridBanners: string[], route: string) => {
        if (route === RoutesPath.grid) {
            return gridBanners;
        }

        const keys = Object.keys(banners);

        return keys.filter(key => {
            const banner: EditedBanner = banners[key];

            return (
                banner.isEditAllow &&
                (banner.bannerType === AdvType.TEXT || banner.bannerType === AdvTypeClient.TEXT_MOBILE)
            );
        });
    }
);

export const selectPriceEditableIds = selectEditableTextAdsIds;
export const selectOrganizationEditableIds = selectEditableTextAdsIds;

export const selectLinkReplacementValid = createSelector(
    (state: State) => state.bannersEditMassActions[MassActionId.bannersEdit].errors,
    (massErrors: ErrorItem[]) => {
        return !(
            massErrors &&
            massErrors.length !== 0 &&
            massErrors.find(item => item.path === 'link-replacement-input')
        );
    }
);

export const selectAllBannersLinks = createSelector(
    (state: State) => state.editBanners.bannersDraft,
    (state: State) => state.editBanners.banners,
    (state: State) => state.siteLinksEditor.data,
    (state: State, isSiteLinkEnabled: boolean) => isSiteLinkEnabled,
    (
        bannersDraft: Record<BannerId, DraftBanner>,
        banners: Record<BannerId, EditedBanner>,
        siteLinksData: Record<BannerSiteLinksId, BannerSiteLinksEditorData>,
        isSiteLinksEnabled: boolean | undefined
    ): DropdownChooserItem[] => {
        const items = Object.keys(banners).reduce(
            (acc: DropdownChooserItem[], next: BannerId) => {
                const currentBanner = banners[next];
                const isHrefHidden = currentBanner.campaign.isOnlyTurboAvailable;

                if (isBannerEditMassActionsAvailable(currentBanner)) {
                    const { href, hrefProtocol } = currentBanner.contacts;
                    const url = UrlHelpers.concatHrefToProtocol(href, hrefProtocol);

                    if (href) {
                        acc.push({
                            name: url,
                            id: url
                        });
                    } else {
                        acc.push({
                            name: '',
                            id: ''
                        });
                    }

                    if (!isHrefHidden && isSiteLinksEnabled && bannerHasSitelinks(currentBanner)) {
                        const uniqSiteLinks = uniq(
                            getAllVisibleBannerSiteLinksUrls(currentBanner, siteLinksData[next])
                        ).map(siteLinkUrl => ({
                            name: siteLinkUrl,
                            id: siteLinkUrl
                        }));

                        acc.push(...uniqSiteLinks);
                    }
                }

                return acc;
            },
            [] as DropdownChooserItem[]
        );

        if (items.some(item => item.id === '')) {
            items.unshift({
                id: '',
                name: i18n('select-empty')
            });
        }

        return uniqBy(items, 'id');
    }
);

export const selectIdsBySelectedText = createSelector(
    (state: State) => state.editBanners.banners,
    (state: State) => state.editBanners.bannersDraft,
    (state: State) => state.siteLinksEditor.data,
    (state: State) => state.bannersEditMassActions[MassActionId.bannersEdit].data.sourceText.toLowerCase(),
    (state: State) => state.bannersEditMassActions[MassActionId.bannersEdit].data.isTitleTumblerChecked,
    (state: State) => state.bannersEditMassActions[MassActionId.bannersEdit].data.isAdditionalTextTumblerChecked,
    (state: State) => state.bannersEditMassActions[MassActionId.bannersEdit].data.isSiteLinksTumblerChecked,
    (
        banners: Record<BannerId, EditedBanner>,
        bannersDraft: Record<BannerId, DraftBanner>,
        siteLinksData: Record<BannerSiteLinksId, BannerSiteLinksEditorData>,
        sourceText: string,
        isTitleTumblerChecked: boolean,
        isAdditionalTextTumblerChecked: boolean,
        isSiteLinksTumblerChecked: boolean
    ) => {
        return Object.keys(banners).reduce(
            (acc: BannerId[], next: BannerId) => {
                const currentBanner = banners[next];

                if (
                    isBannerEditMassActionsAvailable(currentBanner) &&
                    isTextReplaceMassActionsAvailable(currentBanner)
                ) {
                    const { title, titleExtension, body } = currentBanner;

                    if (
                        isTitleTumblerChecked &&
                        (title.toLowerCase().includes(sourceText) || titleExtension.toLowerCase().includes(sourceText))
                    ) {
                        acc.push(next);
                    }

                    if (isAdditionalTextTumblerChecked && body.toLowerCase().includes(sourceText)) {
                        acc.push(next);
                    }

                    if (isSiteLinksTumblerChecked) {
                        const siteLinksText = getAllVisibleBannerSiteLinksText(currentBanner, siteLinksData[next]);
                        const siteLinksDescr = getAllVisibleBannerSiteLinksDescr(currentBanner, siteLinksData[next]);

                        const isSiteLinksChoosen =
                            [...siteLinksText, ...siteLinksDescr].filter(item =>
                                item.toLowerCase().includes(sourceText)
                            ).length !== 0;

                        if (isSiteLinksChoosen) {
                            acc.push(next);
                        }
                    }
                }

                return acc;
            },
            [] as string[]
        );
    }
);

export const selectBannersDisplayedLinks = createSelector(
    (state: State) => state.editBanners.banners,
    (banners: Record<BannerId, EditedBanner>): DropdownChooserItem[] => {
        return Object.keys(banners).reduce(
            (acc: DropdownChooserItem[], next: BannerId) => {
                const currentBanner = banners[next];

                if (isBannerTextMassActionsAvailable(currentBanner)) {
                    const { displayHref } = currentBanner.contacts;

                    const currentTextAcc = acc.map(val => val.id);

                    if (!currentTextAcc.includes(displayHref)) {
                        acc.push({
                            name: displayHref !== '' ? displayHref : i18n('select-empty'),
                            id: displayHref
                        });
                    }
                }

                return acc;
            },
            [] as DropdownChooserItem[]
        );
    }
);

export const selectIdsBySelectedUrls = createSelector(
    (state: State) => state.editBanners.banners,
    (state: State) => state.editBanners.bannersDraft,
    (state: State) => state.siteLinksEditor.data,
    (state: State) => state.bannersEditMassActions[MassActionId.bannersEdit].data.selectedSourceItems,
    (state: State) => state.bannersEditMassActions[MassActionId.bannersEdit].data.isSiteLinksTumblerChecked,
    (
        banners: Record<BannerId, EditedBanner>,
        bannersDraft: Record<BannerId, DraftBanner>,
        siteLinksData: Record<BannerSiteLinksId, BannerSiteLinksEditorData>,
        urlsLinks: string[],
        isSiteLinksEnabled: boolean
    ) => {
        return Object.keys(banners).reduce(
            (acc: BannerId[], next: BannerId) => {
                const currentBanner = banners[next];
                const isHrefHidden = currentBanner.campaign.isOnlyTurboAvailable;

                if (isBannerEditMassActionsAvailable(currentBanner)) {
                    const { href, hrefProtocol } = currentBanner.contacts;
                    const url = UrlHelpers.concatHrefToProtocol(href, hrefProtocol);

                    if (urlsLinks.includes(url)) {
                        acc.push(next);
                    } else if (!isHrefHidden && isSiteLinksEnabled && bannerHasSitelinks(currentBanner)) {
                        const siteLinksUrls = getAllVisibleBannerSiteLinksUrls(currentBanner, siteLinksData[next]);

                        const isSiteLinksChoosen = siteLinksUrls.filter(item => urlsLinks.includes(item)).length !== 0;

                        if (isSiteLinksChoosen) {
                            acc.push(next);
                        }
                    }
                }

                return acc;
            },
            [] as string[]
        );
    }
);

export const selectIdsBySelectedDisplayedLinks = createSelector(
    (state: State) => state.editBanners.banners,
    (state: State) => state.bannersEditMassActions[MassActionId.bannersEdit].data.selectedSourceItems,
    (banners: Record<BannerId, EditedBanner>, urlsLinks: string[]) => {
        return Object.keys(banners).reduce(
            (acc: BannerId[], next: BannerId) => {
                const currentBanner = banners[next];

                if (isBannerEditMassActionsAvailable(currentBanner)) {
                    const { displayHref } = currentBanner.contacts;

                    if (urlsLinks.includes(displayHref)) {
                        acc.push(next);
                    }
                }

                return acc;
            },
            [] as string[]
        );
    }
);

export const selectBannersDraftEditSiteLinksDataByLinkReplace = createSelector(
    (state: State) => state.editBanners.banners,
    (state: State) => state.editBanners.bannersDraft,
    (state: State) => state.siteLinksEditor.data,
    (state: State) => state.bannersEditMassActions[MassActionId.bannersEdit].data.selectedSourceItems,
    (state: State) => state.bannersEditMassActions[MassActionId.bannersEdit].data.selectedProtocolValue,
    (state: State) => state.bannersEditMassActions[MassActionId.bannersEdit].data.newVisibleLink,
    (state: State) => state.bannersEditMassActions[MassActionId.bannersEdit].data.isSiteLinksTumblerChecked,
    selectIdsBySelectedUrls,
    (
        banners: Record<BannerId, EditedBanner>,
        bannersDraft: Record<BannerId, DraftBanner>,
        siteLinksData: Record<BannerSiteLinksId, BannerSiteLinksEditorData>,
        selectedSourceUrls: string[],
        selectedProtocolValue: ProtocolValue,
        newVisibleLink: string,
        isSiteLinkEnabled: boolean,
        bannersIds: BannerId[]
    ) => {
        if (!isSiteLinkEnabled) {
            return siteLinksData;
        }

        return Object.keys(siteLinksData).reduce(
            (acc: Record<BannerSiteLinksId, BannerSiteLinksEditorData>, next: BannerId) => {
                const currentBanner: BannerSiteLinksEditorData = siteLinksData[next];

                if (bannersIds.includes(next) && !isIdForMassAction(next)) {
                    const originalBanner = banners[next];
                    const isHrefHidden = originalBanner.campaign.isOnlyTurboAvailable;

                    if (!isHrefHidden && bannerHasSitelinks(originalBanner)) {
                        acc[next] = {
                            ...currentBanner,
                            items: getNewBannersSiteLinks(
                                currentBanner,
                                originalBanner,
                                selectedSourceUrls,
                                newVisibleLink,
                                selectedProtocolValue
                            )
                        };
                    }
                } else {
                    acc[next] = currentBanner;
                }

                return acc;
            },
            {} as Record<BannerId, BannerSiteLinksEditorData>
        );
    }
);

export const selectBannersEditDataByLinkReplace = createSelector(
    (state: State) => state.editBanners.banners,
    (state: State) => state.bannersEditMassActions[MassActionId.bannersEdit].data.selectedSourceItems,
    (state: State) => state.bannersEditMassActions[MassActionId.bannersEdit].data.selectedProtocolValue,
    (state: State) => state.bannersEditMassActions[MassActionId.bannersEdit].data.newVisibleLink,
    (state: State) => state.bannersEditMassActions[MassActionId.bannersEdit].data.isSiteLinksTumblerChecked,
    selectIdsBySelectedUrls,
    (state: State, domain: string) => domain,
    (
        banners: Record<BannerId, EditedBanner>,
        selectedSourceUrls: string[],
        selectedProtocolValue: ProtocolValue,
        newVisibleLink: string,
        isSiteLinkEnabled: boolean,
        bannersIds: BannerId[],
        domain: string
    ) => {
        return Object.keys(banners).reduce((acc: Record<BannerId, EditedBanner>, next: BannerId) => {
            let currentBanner = {
                ...banners[next],
                errors: undefined
            };

            if (isBannerEditMassActionsAvailable(currentBanner) && bannersIds.includes(next)) {
                const { contacts } = currentBanner;
                const { href, hrefProtocol } = contacts;

                const url = UrlHelpers.concatHrefToProtocol(href, hrefProtocol);
                if (selectedSourceUrls.includes(url)) {
                    currentBanner = {
                        ...currentBanner,
                        isSaved: false,
                        contacts: {
                            ...contacts,
                            hrefProtocol: selectedProtocolValue,
                            href: newVisibleLink,
                            domain
                        }
                    };
                }

                if (bannerHasSitelinks(currentBanner)) {
                    const isSiteLinksSelected =
                        getSitelinksFullHref(currentBanner.sitelinks.items).some(item =>
                            selectedSourceUrls.includes(item)
                        ) && isSiteLinkEnabled;

                    if (isSiteLinksSelected) {
                        currentBanner = {
                            ...currentBanner,
                            isSaved: false,
                            sitelinks: {
                                ...currentBanner.sitelinks,
                                isOpen: true
                            }
                        };
                    }
                }
            }

            acc[next] = currentBanner;

            return acc;
        }, {});
    }
);

export const selectBannersEditDataByLinkCreativeReplace = createSelector(
    (state: State) => state.editBanners.banners,
    (state: State) => state.bannersEditMassActions[MassActionId.bannersEdit].data.creativeAdIds,
    (state: State) => state.bannersEditMassActions[MassActionId.bannersEdit].data.selectedProtocolValue,
    (state: State) => state.bannersEditMassActions[MassActionId.bannersEdit].data.newVisibleLink,
    (
        banners: Record<BannerId, EditedBanner>,
        creativeAdIds: BannerId[],
        selectedProtocolValue: ProtocolValue,
        newVisibleLink: string
    ) => {
        return creativeAdIds.reduce((acc: Record<BannerId, EditedBanner>, next: BannerId) => {
            let currentBanner = {
                ...banners[next],
                errors: undefined
            };

            if (isBannerEditMassActionsAvailable(currentBanner)) {
                const { contacts } = currentBanner;

                currentBanner = {
                    ...currentBanner,
                    isSaved: false,
                    contacts: {
                        ...contacts,
                        hrefProtocol: selectedProtocolValue,
                        href: newVisibleLink
                    }
                };
            }

            acc[next] = currentBanner;

            return acc;
        }, {});
    }
);

export const selectBannersEditPrices = createSelector(
    (state: State) => state.editBanners.banners,
    (state: State) => state.bannersPriceEditor[MassActionId.bannersEdit],
    (banners: Record<BannerId, EditedBanner>, bannerPriceEditor: EditorBannerPriceData) => {
        return Object.keys(banners).reduce(
            (acc, next) => {
                if (isBannerPriceMassActionsAvailable(banners[next])) {
                    const banner = banners[next] as EditedBannerTypeText;

                    acc[next] = {
                        ...banner,
                        bannerPriceEditor: {
                            ...banner.bannerPriceEditor,
                            bannerPrice: bannerPriceEditor.bannerPrice,
                            isOpen: false
                        },
                        errors: undefined
                    };
                } else {
                    acc[next] = banners[next];
                }

                return acc;
            },
            {} as Record<BannerId, EditedBanner>
        );
    }
);

export const selectBannersEditDataByDisplayedLinkReplace = createSelector(
    (state: State) => state.editBanners.banners,
    (state: State) => state.bannersEditMassActions[MassActionId.bannersEdit].data.selectedSourceItems,
    (state: State) => state.bannersEditMassActions[MassActionId.bannersEdit].data.newVisibleLink,
    selectIdsBySelectedDisplayedLinks,
    (
        banners: Record<BannerId, EditedBanner>,
        selectedSourceItems: string[],
        newVisibleLink: string,
        bannersIds: BannerId[]
    ) => {
        return Object.keys(banners).reduce(
            (acc: Record<BannerId, EditedBanner>, next: BannerId) => {
                const currentBanner = {
                    ...banners[next],
                    errors: undefined
                };

                if (isBannerEditMassActionsAvailable(currentBanner) && bannersIds.includes(next)) {
                    const { contacts } = currentBanner;
                    const { displayHref } = contacts;

                    if (selectedSourceItems.includes(displayHref)) {
                        acc[next] = {
                            ...currentBanner,
                            isSaved: false,
                            contacts: {
                                ...contacts,
                                displayHref: newVisibleLink.replace(/\s+/g, '-')
                            }
                        };
                    }
                } else {
                    acc[next] = banners[next];
                }

                return acc;
            },
            {} as Record<BannerId, EditedBanner>
        );
    }
);

export const selectCpmCreativeDataByCreativeReplace = createSelector(
    (state: State) => state.editBanners.banners,
    (state: State) => state.creative,
    (banners: Record<BannerId, EditedBanner>, creative: CreativeState) => {
        const newCreative = creative[MassActionId.bannersEdit];

        if (newCreative.additionalData) {
            const additionalData = newCreative.additionalData;

            return Object.keys(banners).reduce(
                (acc: Record<EditorPlaceId, EditedBannerCreative>, next: BannerId) => {
                    // Поисх подходящего креатива
                    const resCreative = additionalData.filter(curCreative =>
                        checkCreative(banners[next], { ...newCreative, data: curCreative }, creative[next])
                    )[0];

                    if (resCreative) {
                        acc[next] = { ...newCreative, data: resCreative };
                    }

                    return acc;
                },
                {} as Record<EditorPlaceId, EditedBannerCreative>
            );
        }

        return Object.keys(banners).reduce(
            (acc: Record<EditorPlaceId, EditedBannerCreative>, next: BannerId) => {
                if (checkCreative(banners[next], newCreative, creative[next])) {
                    acc[next] = newCreative;
                }

                return acc;
            },
            {} as Record<EditorPlaceId, EditedBannerCreative>
        );
    }
);

export const selectVideoDataByVideoReplace = createSelector(
    (state: State) => state.editBanners.banners,
    (state: State, massActionId: MassActionId) => state.video[massActionId],
    (banners: Record<BannerId, EditedBanner>, newVideo: EditedBannerVideo) => {
        return Object.keys(banners).reduce(
            (acc: Record<EditorPlaceId, EditedBannerVideo>, next: BannerId) => {
                if (isTextBannerEditMassActionsAvailable(banners[next])) {
                    acc[next] = newVideo;
                }

                return acc;
            },
            <Record<EditorPlaceId, EditedBannerVideo>>{}
        );
    }
);

export const selectVideoCreativeDataByVideoReplace = createSelector(
    (state: State) => state.editBanners.banners,
    (state: State, massActionId: MassActionId) => state.video,
    (state: State) => state.bannersEditMassActions.bannersEdit.typeCreative,
    (
        banners: Record<BannerId, EditedBanner>,
        newVideo: Record<EditorPlaceId, EditedBannerVideo>,
        bannerType: AdvType
    ) => {
        return Object.keys(banners).reduce(
            (acc: Record<EditorPlaceId, EditedBannerVideo>, next: BannerId) => {
                if (isVideoBannerEditMassActionsAvailable(banners[next], bannerType)) {
                    const massVideo = newVideo[MassActionId.bannersEdit];

                    if (massVideo.data) {
                        acc[next] = massVideo;
                    } else {
                        acc[next] = newVideo[next];
                    }
                }

                return acc;
            },
            <Record<EditorPlaceId, EditedBannerVideo>>{}
        );
    }
);

export const selectTurbolandingDataByTurbolandingReplace = createSelector(
    (state: State) => state.editBanners.banners,
    (state: State, massActionId: MassActionId) => state.turbolandings[massActionId],
    (banners: Record<BannerId, EditedBanner>, newTurbolanding: EditedBannerTurbolanding) => {
        return Object.keys(banners).reduce(
            (acc: Record<EditorPlaceId, EditedBannerTurbolanding>, next: BannerId) => {
                if (isBannerEditMassActionsAvailable(banners[next])) {
                    acc[next] = newTurbolanding;
                }

                return acc;
            },
            {} as Record<EditorPlaceId, EditedBannerTurbolanding>
        );
    }
);

export const selectBannersEditDataByTextReplace = createSelector(
    (state: State) => state.editBanners.banners,
    (state: State) => state.editBanners.bannersDraft,
    (state: State) => state.siteLinksEditor.data,
    (state: State) => state.bannersEditMassActions[MassActionId.bannersEdit].data.sourceText.toLowerCase(),
    (state: State) => state.bannersEditMassActions[MassActionId.bannersEdit].data.newText,
    (state: State) => state.bannersEditMassActions[MassActionId.bannersEdit].data.isTitleTumblerChecked,
    (state: State) => state.bannersEditMassActions[MassActionId.bannersEdit].data.isAdditionalTextTumblerChecked,
    (state: State) => state.bannersEditMassActions[MassActionId.bannersEdit].data.isSiteLinksTumblerChecked,
    selectIdsBySelectedText,
    (
        banners: Record<BannerId, EditedBanner>,
        bannersDraft: Record<BannerId, DraftBanner>,
        siteLinksData: Record<BannerSiteLinksId, BannerSiteLinksEditorData>,
        sourceText: string,
        newText: string,
        isTitleTumblerChecked: boolean,
        isAdditionalTextTumblerChecked: boolean,
        isSiteLinksTumblerChecked: boolean,
        bannersIds: BannerId[]
    ) => {
        const sourceTextRegExp = new RegExp(escapeRegexpString(sourceText), 'gi');

        return Object.keys(banners).reduce(
            (acc: Record<BannerId, EditedBanner>, next: BannerId) => {
                const currentBanner = {
                    ...banners[next],
                    errors: undefined
                };

                if (isTextReplaceMassActionsAvailable(currentBanner) && bannersIds.includes(next)) {
                    const { body, title, titleExtension } = currentBanner;

                    acc[next] = currentBanner;

                    if (isTitleTumblerChecked) {
                        const newTitle = title.toLowerCase().includes(sourceText)
                            ? title.replace(sourceTextRegExp, newText)
                            : title;
                        const newTitleExtension = titleExtension.toLowerCase().includes(sourceText)
                            ? titleExtension.toLowerCase().replace(sourceTextRegExp, newText)
                            : titleExtension;

                        acc[next] = {
                            ...currentBanner,
                            isSaved: false,
                            title: newTitle,
                            titleExtension: newTitleExtension
                        } as EditedBannerTypeText;
                    }

                    if (isAdditionalTextTumblerChecked) {
                        const newBody = body.toLowerCase().includes(sourceText)
                            ? body.replace(sourceTextRegExp, newText)
                            : body;

                        acc[next] = {
                            ...acc[next],
                            isSaved: false,
                            body: newBody
                        } as EditedBannerTypeText;
                    }

                    if (isSiteLinksTumblerChecked && bannerHasSitelinks(currentBanner)) {
                        acc[next] = {
                            ...acc[next],
                            isSaved: false,
                            sitelinks: {
                                ...currentBanner.sitelinks,
                                isOpen: true
                            }
                        } as EditedBannerTypeText;
                    }
                } else {
                    acc[next] = banners[next];
                }

                return acc;
            },
            {} as Record<BannerId, EditedBanner>
        );
    }
);

export const selectBannersDraftEditDataByTextReplace = createSelector(
    (state: State) => state.editBanners.banners,
    (state: State) => state.editBanners.bannersDraft,
    (state: State) => state.siteLinksEditor.data,
    (state: State) => state.bannersEditMassActions[MassActionId.bannersEdit].data.sourceText.toLowerCase(),
    (state: State) => state.bannersEditMassActions[MassActionId.bannersEdit].data.newText,
    (state: State) => state.bannersEditMassActions[MassActionId.bannersEdit].data.isSiteLinksTumblerChecked,
    selectIdsBySelectedText,
    (
        banners: Record<BannerId, EditedBanner>,
        bannersDraft: Record<BannerId, DraftBanner>,
        siteLinksData: Record<BannerSiteLinksId, BannerSiteLinksEditorData>,
        sourceText: string,
        newText: string,
        isSiteLinksTumblerChecked: boolean,
        bannersIds: BannerId[]
    ) => {
        if (!isSiteLinksTumblerChecked) {
            return siteLinksData;
        }

        const sourceTextRegExp = new RegExp(escapeRegexpString(sourceText), 'gi');

        return Object.keys(siteLinksData).reduce(
            (acc: Record<BannerSiteLinksId, BannerSiteLinksEditorData>, next: BannerId) => {
                const siteLinkData: BannerSiteLinksEditorData = siteLinksData[next];
                const banner: EditedBanner = banners[next];

                acc[next] = siteLinkData;

                if (!isIdForMassAction(next) && bannersIds.includes(next) && bannerHasSitelinks(banner)) {
                    let bannersSiteLinksItems = siteLinkData.items;

                    if (!banner.sitelinks.isOpen) {
                        bannersSiteLinksItems = banner.sitelinks.items;
                    }

                    acc[next] = {
                        ...acc[next],
                        items: bannersSiteLinksItems.map(item => {
                            const { title, descr } = item;
                            let data = {
                                ...item,
                                title: title.toLowerCase().includes(sourceText)
                                    ? title.replace(sourceTextRegExp, newText)
                                    : title
                            };

                            if (siteLinkData.isDescriptionChecked) {
                                data = {
                                    ...data,
                                    descr: descr.toLowerCase().includes(sourceText)
                                        ? descr.replace(sourceTextRegExp, newText)
                                        : descr
                                };
                            }

                            return data;
                        })
                    };
                }

                return acc;
            },
            {} as Record<BannerId, BannerSiteLinksEditorData>
        );
    }
);

export const selectBannersEditDataByPartLinkReplace = createSelector(
    (state: State) => state.editBanners.banners,
    (state: State) => state.bannersEditMassActions[MassActionId.bannersEdit].data.newPartUrl,
    (state: State) => state.bannersEditMassActions[MassActionId.bannersEdit].data.targetPartUrl,
    (state: State) => state.bannersEditMassActions[MassActionId.bannersEdit].data.isParametersTumblerChecked,
    (state: State) => state.bannersEditMassActions[MassActionId.bannersEdit].data.isDomainAndPathTumblerChecked,
    (state: State) => state.bannersEditMassActions[MassActionId.bannersEdit].data.isSiteLinksTumblerChecked,
    (state: State, domains: Record<BannerId, string> | undefined) => domains,
    (
        banners: Record<BannerId, EditedBanner>,
        newPartUrl: string,
        targetPartUrl: string,
        isParametersTumblerChecked: boolean,
        isDomainAndPathTumblerChecked: boolean,
        isSiteLinkEnabled: boolean,
        domains?: Record<BannerId, string>
    ) => {
        return Object.keys(banners).reduce((acc: Record<BannerId, EditedBanner>, next: BannerId) => {
            const currentBanner = {
                ...banners[next],
                errors: undefined
            };
            const urlPart: UrlPart[] = [];

            if (isDomainAndPathTumblerChecked) {
                urlPart.push(UrlPart.DOMAIN_AND_PATH);
            }

            if (isParametersTumblerChecked) {
                urlPart.push(UrlPart.PARAMS);
            }

            if (isBannerEditMassActionsAvailable(currentBanner)) {
                const { contacts } = currentBanner;
                const { href } = contacts;
                const newVisibleLink = replaceUrlPart(href, {
                    from: targetPartUrl,
                    to: newPartUrl,
                    urlPart: urlPart
                });

                acc[next] = {
                    ...currentBanner,
                    isSaved: false,
                    contacts: {
                        ...contacts,
                        href: newVisibleLink,
                        domain: domains ? domains[next] || '' : contacts.domain
                    }
                };

                if (bannerHasSitelinks(currentBanner)) {
                    const isSiteLinksSelected =
                        siteLinksIncludes(currentBanner.sitelinks.items, targetPartUrl, urlPart) && isSiteLinkEnabled;

                    if (isSiteLinksSelected) {
                        acc[next] = {
                            ...(acc[next] || {}),
                            isSaved: false,
                            sitelinks: {
                                ...currentBanner.sitelinks,
                                isOpen: true
                            }
                        } as EditedBannerTypeText;
                    }
                }
            } else {
                acc[next] = currentBanner;
            }

            return acc;
        }, {});
    }
);

export const selectBannersDraftEditSiteLinksDataByPartLinkReplace = createSelector(
    (state: State) => state.editBanners.banners,
    (state: State) => state.editBanners.bannersDraft,
    (state: State) => state.siteLinksEditor.data,
    (state: State) => state.bannersEditMassActions[MassActionId.bannersEdit].data.newPartUrl,
    (state: State) => state.bannersEditMassActions[MassActionId.bannersEdit].data.targetPartUrl,
    (state: State) => state.bannersEditMassActions[MassActionId.bannersEdit].data.isParametersTumblerChecked,
    (state: State) => state.bannersEditMassActions[MassActionId.bannersEdit].data.isDomainAndPathTumblerChecked,
    (state: State) => state.bannersEditMassActions[MassActionId.bannersEdit].data.isSiteLinksTumblerChecked,
    (
        banners: Record<BannerId, EditedBanner>,
        bannersDraft: Record<BannerId, DraftBanner>,
        siteLinksData: Record<BannerSiteLinksId, BannerSiteLinksEditorData>,
        newPartUrl: string,
        targetPartUrl: string,
        isParametersTumblerChecked: boolean,
        isDomainAndPathTumblerChecked: boolean,
        isSiteLinkEnabled: boolean
    ) => {
        if (!isSiteLinkEnabled) {
            return siteLinksData;
        }

        return Object.keys(siteLinksData).reduce(
            (acc: Record<BannerSiteLinksId, BannerSiteLinksEditorData>, next: BannerId) => {
                const currentSiteLinks: BannerSiteLinksEditorData = siteLinksData[next];
                const urlPart: UrlPart[] = [];

                if (isDomainAndPathTumblerChecked) {
                    urlPart.push(UrlPart.DOMAIN_AND_PATH);
                }

                if (isParametersTumblerChecked) {
                    urlPart.push(UrlPart.PARAMS);
                }

                const banner = banners[next];

                if (banner && !isIdForMassAction(next) && bannerHasSitelinks(banner)) {
                    acc[next] = {
                        ...currentSiteLinks,
                        items: getNewBannersSiteLinksForPartLinkReplace(
                            currentSiteLinks,
                            banner,
                            newPartUrl,
                            targetPartUrl,
                            urlPart
                        ),
                        isDescriptionChecked: true
                    };
                }

                return acc;
            },
            {}
        );
    }
);

export const selectSiteLinksEditDataBySiteLinksReplace = createSelector(
    (state: State) => state.editBanners.banners,
    (state: State) => state.siteLinksEditor.data,
    (state: State) => state.bannersEditMassActions[MassActionId.bannersEdit].data.currentSiteLinksActiveRadioButton,
    (
        banners: Record<BannerId, EditedBanner>,
        siteLinksEditor: Record<BannerSiteLinksId, BannerSiteLinksEditorData>,
        currentSiteLinksActiveRadioButton: BulkReplacementRadio
    ) => {
        return Object.keys(banners).reduce(
            (acc: Record<BannerSiteLinksId, BannerSiteLinksEditorData>, next: BannerId) => {
                const massActionsSiteLinks = siteLinksEditor[MassActionId.bannersEdit];
                const banner = banners[next];
                const isHrefHidden = banner.campaign.isOnlyTurboAvailable;
                const siteLinksDataFromEditor = siteLinksEditor[next];

                if (currentSiteLinksActiveRadioButton === BulkReplacementRadio.DELETE) {
                    acc[next] = {
                        ...massActionsSiteLinks,
                        items: range(4).map(getDefaultSiteLinkData),
                        errors: {}
                    };
                } else {
                    if (isBannerEditMassActionsAvailable(banner) && bannerHasSitelinks(banner)) {
                        // сначала матчим совпадающее количество сайтлинков в баннере и
                        // массовых изменениях
                        const comparedSiteLinks = massActionsSiteLinks.items.map((massActionSiteLink, index) => {
                            const siteLinkFromEditor = siteLinksDataFromEditor.items[index] || getDefaultSiteLinkData();

                            const title =
                                massActionSiteLink.isMassActionsTitleDifferent && !massActionSiteLink.title
                                    ? siteLinkFromEditor.title
                                    : massActionSiteLink.title;
                            const descr =
                                massActionSiteLink.isMassActionsDescrDifferent && !massActionSiteLink.descr
                                    ? siteLinkFromEditor.descr
                                    : massActionSiteLink.descr;
                            const link =
                                massActionSiteLink.isMassActionsLinkDifferent && !massActionSiteLink.link
                                    ? siteLinkFromEditor.link
                                    : massActionSiteLink.link;
                            const turbo =
                                massActionSiteLink.isMassActionsTurboDifferent && !massActionSiteLink.turbo
                                    ? siteLinkFromEditor.turbo
                                    : massActionSiteLink.turbo;
                            const protocol =
                                massActionSiteLink.isMassActionsProtocolDifferent &&
                                massActionSiteLink.protocol === ProtocolValue.different
                                    ? siteLinkFromEditor.protocol
                                    : massActionSiteLink.protocol;

                            return {
                                ...siteLinkFromEditor,
                                title,
                                descr,
                                link: isHrefHidden ? undefined : link,
                                turbo,
                                protocol
                            };
                        });

                        acc[next] = {
                            ...massActionsSiteLinks,
                            items: getCollapsedSiteLinks(comparedSiteLinks)
                        };
                    } else {
                        acc[next] = massActionsSiteLinks;
                    }
                }

                return acc;
            },
            {} as Record<BannerSiteLinksId, BannerSiteLinksEditorData>
        );
    }
);

export const selectBannersEditDataBySiteLinksReplace = createSelector(
    (state: State) => state.editBanners.banners,
    (banners: Record<BannerId, EditedBanner>) => {
        return Object.keys(banners).reduce(
            (acc: Record<BannerId, EditedBanner>, next: BannerId) => {
                const currentBanner = {
                    ...banners[next],
                    errors: undefined
                };

                if (isBannerEditMassActionsAvailable(currentBanner) && bannerHasSitelinks(currentBanner)) {
                    acc[next] = {
                        ...currentBanner,
                        isSaved: false,
                        sitelinks: {
                            ...currentBanner.sitelinks,
                            isOpen: true
                        }
                    };
                } else {
                    acc[next] = currentBanner;
                }

                return acc;
            },
            {} as Record<BannerId, EditedBanner>
        );
    }
);

export const selectBannersMassActionsWorkCurrency = createSelector(
    (state: State) => state.router.route,
    (state: State) => state.editBanners.clientInfo.workCurrency,
    (state: State) => state.grid.clientInfo.workCurrency,
    (route: string, editBannersCurrency: CurrencyCode, gridCurrency: CurrencyCode): CurrencyCode => {
        if (route === RoutesPath.grid) {
            return gridCurrency;
        }

        if (route === RoutesPath.bannersEdit) {
            return editBannersCurrency;
        }

        throw 'Wrong selector usage';
    }
);

export const selectTurbolandingDataByTrackingParametersReplace = createSelector(
    (state: State) => state.turbolandings,
    (state: State) => state.editBanners.banners,
    (state: State) => state.bannersEditMassActions[MassActionId.bannersEdit].data.newParameters,
    (state: State) => state.bannersEditMassActions[MassActionId.bannersEdit].data.targetParameters,
    (state: State) => state.bannersEditMassActions[MassActionId.bannersEdit].data.keyOrValueReplacement,
    (
        turbolandings: Record<EditorPlaceId, EditedBannerTurbolanding>,
        banners: Record<BannerId, EditedBanner>,
        newParameters: string,
        targetParameters: string,
        keyOrValueReplacement: TrackingParametersReplacementSelect
    ) => {
        return Object.keys(turbolandings).reduce(
            (acc: Record<EditorPlaceId, EditedBannerTurbolanding>, next: EditorPlaceId) => {
                const turbo = turbolandings[next];
                acc[next] = turbo;

                if (next === MassActionId.bannersEdit || next === MassActionId.bannerGrid) {
                    return acc;
                }

                const currentBanner = banners[next];

                if (currentBanner && isBannerEditMassActionsAvailable(currentBanner)) {
                    const { hrefParams = '' } = turbo;
                    const isNeedToChange = hrefTurbolandingIncludesForTrackingParameterReplacement(
                        hrefParams,
                        targetParameters,
                        keyOrValueReplacement
                    );
                    // a={b}

                    if (isNeedToChange) {
                        const newVisibleLink = replaceTurboTrackingParameters(hrefParams, {
                            from: targetParameters,
                            to: newParameters,
                            keyOrValueReplacement
                        });

                        acc[next] = {
                            ...turbo,
                            hrefParams: newVisibleLink
                        };
                    }
                }

                return acc;
            },
            {} as Record<EditorPlaceId, EditedBannerTurbolanding>
        );
    }
);

export const selectBannersEditDataByTrackingParametersReplace = createSelector(
    (state: State) => state.editBanners.banners,
    (state: State) => state.bannersEditMassActions[MassActionId.bannersEdit].data.newParameters,
    (state: State) => state.bannersEditMassActions[MassActionId.bannersEdit].data.targetParameters,
    (state: State) => state.bannersEditMassActions[MassActionId.bannersEdit].data.keyOrValueReplacement,
    (state: State) => state.bannersEditMassActions[MassActionId.bannersEdit].data.isSiteLinksTumblerChecked,
    (
        banners: Record<BannerId, EditedBanner>,
        newParameters: string,
        targetParameters: string,
        keyOrValueReplacement: TrackingParametersReplacementSelect,
        isSiteLinkEnabled: boolean
    ) => {
        return Object.keys(banners).reduce(
            (acc: Record<BannerId, EditedBanner>, next: BannerId) => {
                const currentBanner = {
                    ...banners[next],
                    errors: undefined
                };

                acc[next] = currentBanner;

                if (isBannerEditMassActionsAvailable(currentBanner)) {
                    const { contacts } = currentBanner;
                    const { href } = contacts;
                    const isNeedToChange = hrefIncludesForTrackingParameterReplacement(
                        href,
                        targetParameters,
                        keyOrValueReplacement
                    );

                    if (isNeedToChange) {
                        const newVisibleLink = replaceTrackingParameters(href, {
                            from: targetParameters,
                            to: newParameters,
                            keyOrValueReplacement
                        });

                        acc[next] = {
                            ...acc[next],
                            isSaved: false,
                            contacts: {
                                ...contacts,
                                href: newVisibleLink
                            }
                        } as EditedBannerTypeText;
                    }

                    if (bannerHasSitelinks(currentBanner)) {
                        const isSiteLinksSelected =
                            siteLinksIncludesForTrackingParameterReplacement(
                                currentBanner.sitelinks.items,
                                targetParameters,
                                keyOrValueReplacement
                            ) && isSiteLinkEnabled;

                        if (isSiteLinksSelected) {
                            acc[next] = {
                                ...acc[next],
                                isSaved: false,
                                sitelinks: {
                                    ...currentBanner.sitelinks,
                                    isOpen: true
                                }
                            } as EditedBannerTypeText;
                        }
                    }
                }

                return acc;
            },
            {} as Record<BannerId, EditedBanner>
        );
    }
);

export const selectBannersDraftEditSiteLinksDataByTrackingParametersReplace = createSelector(
    (state: State) => state.editBanners.banners,
    (state: State) => state.siteLinksEditor.data,
    (state: State) => state.bannersEditMassActions[MassActionId.bannersEdit].data.newParameters,
    (state: State) => state.bannersEditMassActions[MassActionId.bannersEdit].data.targetParameters,
    (state: State) => state.bannersEditMassActions[MassActionId.bannersEdit].data.keyOrValueReplacement,
    (state: State) => state.bannersEditMassActions[MassActionId.bannersEdit].data.isSiteLinksTumblerChecked,
    (
        banners: Record<BannerId, EditedBanner>,
        siteLinksData: Record<BannerSiteLinksId, BannerSiteLinksEditorData>,
        newParameters: string,
        targetParameters: string,
        keyOrValueReplacement: TrackingParametersReplacementSelect,
        isSiteLinkEnabled: boolean
    ) => {
        if (!isSiteLinkEnabled) {
            return siteLinksData;
        }

        return Object.keys(siteLinksData).reduce(
            (acc: Record<BannerId, BannerSiteLinksEditorData>, next: BannerId) => {
                const currentBanner: BannerSiteLinksEditorData = siteLinksData[next];
                const banner = banners[next];

                if (banner && next !== MassActionId.bannersEdit && bannerHasSitelinks(banner)) {
                    acc[next] = {
                        ...currentBanner,
                        items: getNewBannersSiteLinksForTrackingParametersReplace(
                            currentBanner,
                            banner,
                            newParameters,
                            targetParameters,
                            keyOrValueReplacement
                        )
                    };
                } else {
                    acc[next] = currentBanner;
                }

                return acc;
            },
            {} as Record<BannerId, BannerSiteLinksEditorData>
        );
    }
);

export const selectAvailableBannersIdsByCallouts = createSelector(
    (state: State) => state.editBanners.banners,
    (banners: Record<BannerId, EditedBanner>) => {
        return Object.keys(banners).filter(key => isBannerEditMassActionsAvailable(banners[key]));
    }
);

export const selectMassActionsSelectedCallouts = createSelector(
    (state: State) => state.editBanners.banners,
    selectAvailableBannersIdsByCallouts,
    (banners: Record<BannerId, EditedBanner>, bannersIds: string[]): string[] => {
        const allBannersCallouts = Object.keys(banners).map(next => {
            const banner = banners[next];

            if (bannersIds.includes(next) && isBannerEditMassActionsCalloutsAvailable(banner)) {
                return banner.callouts.selected;
            }

            return undefined;
        });

        return intersection(...compact(allBannersCallouts));
    }
);

export const selectMassActionsSelectedCalloutsStatus = createSelector(
    (state: State) => state.editBanners.banners,
    selectAvailableBannersIdsByCallouts,
    selectMassActionsSelectedCallouts,
    (state: State) => state.calloutsEditor.data[MassActionId.bannersEdit].selected,
    (
        banners: Record<BannerId, EditedBanner>,
        bannersIds: string[],
        selectedMassActionCalloutsMerge: string[],
        selectedMassActionCallouts: string[]
    ): number => {
        const allBannersCallouts: string[][] = compact(
            Object.keys(banners).map(next => {
                const banner = banners[next];

                if (bannersIds.includes(next) && isBannerEditMassActionsCalloutsAvailable(banner)) {
                    return banner.callouts.selected;
                }

                return undefined;
            })
        );

        return uniq(
            pull(flatten(allBannersCallouts), ...selectedMassActionCallouts, ...selectedMassActionCalloutsMerge)
        ).length;
    }
);

export const selectBannersByCalloutsMassActions = createSelector(
    (state: State) => state.editBanners.banners,
    (state: State) => state.calloutsEditor.data,
    (state: State) => state.bannersEditMassActions[MassActionId.bannersEdit].data.currentCalloutsRadioState,
    selectAvailableBannersIdsByCallouts,
    selectMassActionsSelectedCallouts,
    (
        banners: Record<BannerId, EditedBanner>,
        data: Record<CalloutsEditorId, CalloutsEditorData>,
        currentCalloutsRadioState: BulkReplacementRadio,
        bannersIds: string[],
        selectedMassActionCallouts: string[]
    ) => {
        const { selected } = data[MassActionId.bannersEdit];

        return Object.keys(banners).reduce(
            (acc, next) => {
                const banner = {
                    ...banners[next],
                    errors: undefined
                };

                acc[next] = banner;
                if (bannersIds.includes(next) && isBannerEditMassActionsCalloutsAvailable(banner)) {
                    if (currentCalloutsRadioState === BulkReplacementRadio.DELETE) {
                        acc[next] = {
                            ...banner,
                            callouts: {
                                ...banner.callouts,
                                isEditorOpen: false,
                                selected: []
                            }
                        };
                    } else {
                        acc[next] = {
                            ...banner,
                            callouts: {
                                ...banner.callouts,
                                isEditorOpen: false,
                                selected: uniq([
                                    ...pull(banner.callouts.selected, ...selectedMassActionCallouts),
                                    ...selected
                                ])
                            }
                        };
                    }
                }

                return acc;
            },
            {} as Record<BannerId, EditedBanner>
        );
    }
);

export const selectEditableAllAdsCount = createSelector(
    selectEditableTextAdsCount,
    selectEditableCPCVideoAdsCount,
    selectEditableImageAdsCount,
    selectEditableCPMBannerCount,
    selectEditableCPMVideoCount,
    selectEditableCPMAudioCount,
    selectEditableMcBannerCount,
    (
        bannersTextCount: number,
        bannersVideoCount: number,
        bannersImageCount: number,
        bannersCpmBannerCount: number,
        bannersCpmVideoCount: number,
        bannersCpmAudioCount: number,
        bannersMcBannerCount: number
    ) => {
        return (
            bannersTextCount +
            bannersVideoCount +
            bannersImageCount +
            bannersCpmBannerCount +
            bannersCpmVideoCount +
            bannersCpmAudioCount +
            bannersMcBannerCount
        );
    }
);

export const selectMassActionsCPCVideoInfo = createSelector(
    (state: State, id: EditorPlaceId) => state.video[id],
    (state: State, id: EditorPlaceId) => state.editBanners.clientId,
    (state: State, id: EditorPlaceId) => state.editBanners.constants,
    (state: State, id: EditorPlaceId) => state.editBanners.banners,
    (state: State, id: EditorPlaceId) => state.editBanners.bannersIds,
    (state: State) => state.bannersEditMassActions.bannersEdit.typeCreative,
    (state: State) => state.commonData.features,
    (
        video,
        clientId,
        constants,
        banners,
        bannersIds,
        bannerType: AdvType,
        features: ClientFeatures
    ): EditCPCVideoAdStateProps => {
        const {
            canvas: { canvasDomain }
        } = constants;
        const { isOverlayCreativesEnabled } = features;

        const isNewBanner = false;
        const videoData = video && video.data;

        return {
            creative: {
                name: videoData && videoData.name,
                duration: videoData && videoData.duration,
                formats: videoData && videoData.formats
            },
            canvasDomain,
            pcodeRenderData: mapMassActionDataToPcodeRenderData(BannerViewType.videoAdditionPreview),
            clientId,
            isNewBanner,
            bannerType,
            isCanvasModalOpened: video && video.isCanvasModalOpened,
            isCanvasWithCpcVideo: video && video.isCanvasWithCpcVideo,
            isOverlay: video && video.isOverlay,
            isOverlayCreativesEnabled
        };
    }
);

export const selectMassActionsVideoInfo = createSelector(
    (state: State, id: EditorPlaceId) => state.editBanners.clientId,
    (state: State, id: EditorPlaceId) => state.editBanners.constants,
    (state: State, id: EditorPlaceId) => state.video[id],
    (clientId, constants, video: EditedBannerVideo): EditVideoStateProps => {
        const {
            canvas: { canvasDomain }
        } = constants;

        const videoData: BannerVideoData | undefined = video && video.data;
        const isDefault = Boolean(!(videoData && videoData.id));

        return {
            name: videoData !== undefined ? videoData.name : undefined,
            previewUrl: videoData !== undefined ? videoData.previewUrl : '',
            videoPreviewUrl: isDefault
                ? UrlHelpers.FormatUrl({
                      host: canvasDomain,
                      path: 'video-additions/video-motion/preview',
                      query: { compact: 1, client_id: clientId }
                  })
                : videoData !== undefined
                ? videoData.livePreviewUrl
                : undefined,
            canvasDomain,
            pcodeRenderData: mapMassActionDataToPcodeRenderData(BannerViewType.videoAdditionPreview),
            clientId,
            isDefault,
            isVideoNotShows: false,
            isCanvasModalOpened: video && video.isCanvasModalOpened
        };
    }
);
